import { useStableCallback } from "@prismicio/editor-support/React"
import {
  type KeyboardEvent,
  type RefObject,
  useEffect,
  useLayoutEffect,
  useState,
} from "react"

import {
  ITEM_SELECTOR,
  PICK_EVENT,
  VALID_ITEM_SELECTOR,
  VALUE_ATTR,
} from "./ComboBoxConstants"

interface UseScrollIntoViewProps {
  contentInnerRef: RefObject<HTMLDivElement>
}

export function useScrollIntoView(props: UseScrollIntoViewProps) {
  const { contentInnerRef } = props

  function scrollSelectedIntoView() {
    const selectedItem = getSelectedItem(contentInnerRef)

    if (selectedItem) {
      selectedItem.scrollIntoView({ block: "nearest" })
    }
  }

  useLayoutEffect(() => scrollSelectedIntoView())
}

interface UseInternalSelectionProps {
  contentInnerRef: RefObject<HTMLDivElement>
  inputInnerRef: RefObject<HTMLInputElement>
}
export function useInternalSelection(props: UseInternalSelectionProps) {
  const { contentInnerRef, inputInnerRef } = props

  const [listIsOpen, setListIsOpen] = useState(false)
  const [selectedValue, setSelectedValue] = useState<string>()

  /**
   * Changes internal state selection
   */
  const onChangeInternalSelection = useStableCallback(
    (value: string | undefined) => {
      // Early return if current state matches new value
      if (Object.is(selectedValue, value)) return

      setSelectedValue(value)
    },
  )

  /**
   * Reset internal selection state, close list, blur input
   */
  function cleanupInternalState() {
    inputInnerRef.current?.blur()
    onChangeInternalSelection(undefined)
    setListIsOpen(false)
  }

  /**
   * Updates the internal selection state, selects the next item or the previous item
   */
  function updateInternalSelection(select: "next" | "prev") {
    const selected = getSelectedItem(contentInnerRef)
    const items = getValidItems(contentInnerRef)
    const index = items.findIndex((item) => item === selected)

    // When "next" update the index by 1, else update by -1
    const indexChange = select === "next" ? 1 : -1

    // Get item at this index
    const newSelected = items[index + indexChange]

    if (newSelected)
      onChangeInternalSelection(
        newSelected.getAttribute(VALUE_ATTR) ?? undefined,
      )
  }

  function next(event: KeyboardEvent) {
    event.preventDefault()

    // Next item
    updateInternalSelection("next")
  }

  function prev(event: KeyboardEvent) {
    event.preventDefault()

    // Previous item
    updateInternalSelection("prev")
  }

  /**
   * Dispatches a new PICK Event,
   * This triggers the onPick within the selected comboBox item
   */
  function onItemPicked() {
    const item = getSelectedItem(contentInnerRef)
    if (item) {
      const event = new Event(PICK_EVENT)
      item.dispatchEvent(event)
    }
  }

  return {
    next,
    prev,
    selectedValue,
    listIsOpen,
    setListIsOpen,
    onChangeInternalSelection,
    onItemPicked,
    cleanupInternalState,
  }
}

export function getValidItems(contentInnerRef: RefObject<HTMLDivElement>) {
  return Array.from(
    contentInnerRef.current?.querySelectorAll(VALID_ITEM_SELECTOR) || [],
  )
}

export function getSelectedItem(contentInnerRef: RefObject<HTMLDivElement>) {
  return contentInnerRef.current?.querySelector(
    `${ITEM_SELECTOR}[aria-selected="true"]`,
  )
}

interface UseSelectFirstArgs {
  listIsOpen: boolean
  contentInnerRef: RefObject<HTMLDivElement>
  onChangeInternalSelection: (value: string | undefined) => void
}

/**
 * Selects first option if there is no selected option or the selected option is not available anymore
 * @private
 */
export function useSelectFirst(args: UseSelectFirstArgs) {
  const { listIsOpen, contentInnerRef, onChangeInternalSelection } = args

  useEffect(() => {
    if (!listIsOpen) return

    const contentInnerEl = contentInnerRef.current
    if (!contentInnerEl) return

    function selectFirstItemIfSelectedItemIsNotAvailable() {
      const items = getValidItems(contentInnerRef)
      // no valid options
      if (!items.length) return

      const selectedItem = getSelectedItem(contentInnerRef)
      const selectedItemAvailable =
        items.findIndex((item) => item === selectedItem) > -1

      // selected item is available
      if (selectedItemAvailable) return

      const newSelectedItem = items[0]

      onChangeInternalSelection(
        newSelectedItem?.getAttribute(VALUE_ATTR) ?? undefined,
      )
    }

    const observer = new MutationObserver(([mutation]) => {
      if (mutation && mutation.type === "childList") {
        selectFirstItemIfSelectedItemIsNotAvailable()
      }
    })

    selectFirstItemIfSelectedItemIsNotAvailable()
    observer.observe(contentInnerEl, { childList: true })

    return () => {
      observer.disconnect()
    }
  }, [listIsOpen, contentInnerRef, onChangeInternalSelection])
}
